import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Stack,
  Chip,
  Button,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Switch,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, getFirestore, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { TableSkeleton } from '../components/loading';
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
import firebaseApp from '../utils/firebaseConfig';

const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'categoryIds', label: 'Categories', alignRight: false },
  { id: 'capacity', label: 'Capacity', alignRight: false },
  { id: 'active', label: 'Active', alignRight: false },
  { id: '', label: '', alignRight: false },
];

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_item) => _item.name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => (b[orderBy] < a[orderBy] ? -1 : b[orderBy] > a[orderBy] ? 1 : 0)
    : (a, b) => (a[orderBy] < b[orderBy] ? -1 : a[orderBy] > b[orderBy] ? 1 : 0);
}

const db = getFirestore(firebaseApp);

export default function LanesPage() {
  const [open, setOpen] = useState(null);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('sortOrder');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [lanes, setLanes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [row, setRow] = useState({});
  const [tableLoading, setTableLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setTableLoading(true);
    try {
      const [lanesSnap, catsSnap] = await Promise.all([
        getDocs(collection(db, 'lanes')),
        getDocs(collection(db, 'reservation_categories')),
      ]);
      setLanes(lanesSnap.docs.map((d) => ({ ...d.data(), id: d.id })));
      setCategories(catsSnap.docs.map((d) => ({ ...d.data(), id: d.id })));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setTableLoading(false);
  };

  const getCategoryName = (catId) => {
    const cat = categories.find((c) => c.id === catId);
    return cat ? cat.name : catId;
  };

  const handleOpenMenu = (event, row) => { setOpen(event.currentTarget); setRow(row); };
  const handleCloseMenu = () => setOpen(null);

  const handleEditClick = () => { navigate(`information?id=${row.id}`); handleCloseMenu(); };

  const handleDeleteClick = async () => {
    if (!window.confirm(`Are you sure you want to delete "${row.name}"?`)) return;
    try {
      await deleteDoc(doc(db, 'lanes', row.id));
      setLanes(lanes.filter((l) => l.id !== row.id));
      alert('Lane deleted successfully');
    } catch (error) {
      alert('Error deleting lane');
    }
    handleCloseMenu();
  };

  const handleToggleActive = async (item) => {
    try {
      await updateDoc(doc(db, 'lanes', item.id), { active: !item.active });
      setLanes(lanes.map((l) => (l.id === item.id ? { ...l, active: !l.active } : l)));
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const filteredLanes = applySortFilter(lanes, getComparator(order, orderBy), filterName);

  return (
    <>
      <Helmet>
        <title>Lanes | BBTC Dashboard</title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>Lanes</Typography>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => navigate('information')}>
            New Lane
          </Button>
        </Stack>

        <Card>
          <UserListToolbar filterName={filterName} onFilterName={(e) => { setPage(0); setFilterName(e.target.value); }} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={(e, prop) => {
                    setOrder(orderBy === prop && order === 'asc' ? 'desc' : 'asc');
                    setOrderBy(prop);
                  }}
                />
                <TableBody>
                  {tableLoading && <TableSkeleton rows={5} columns={5} />}
                  {!tableLoading && filteredLanes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                    <TableRow hover key={row.id} tabIndex={-1}>
                      <TableCell padding="checkbox" />
                      <TableCell>
                        <Typography variant="subtitle2" noWrap>{row.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                          {(row.categoryIds || []).map((catId) => (
                            <Chip key={catId} label={getCategoryName(catId)} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>{row.capacity || 1}</TableCell>
                      <TableCell>
                        <Switch checked={row.active || false} onChange={() => handleToggleActive(row)} />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="large" color="inherit" onClick={(e) => handleOpenMenu(e, row)}>
                          <Iconify icon="eva:more-vertical-fill" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!tableLoading && filteredLanes.length === 0 && (
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Typography variant="body2">No lanes found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredLanes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setPage(0); setRowsPerPage(parseInt(e.target.value, 10)); }}
          />
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 1, width: 140 } }}
      >
        <MenuItem onClick={handleEditClick}>
          <Iconify icon="eva:edit-fill" sx={{ mr: 2 }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <Iconify icon="eva:trash-2-outline" sx={{ mr: 2 }} /> Delete
        </MenuItem>
      </Popover>
    </>
  );
}
